import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { AuthService } from '../auth-service/auth-service';

import { loadStripe, Stripe, StripeEmbeddedCheckout } from '@stripe/stripe-js';

export interface StripeCheckoutSession {
  clientSecret: string;
  sessionId: string;
}

export interface PriceTier {
  tier: string;
  price: number;
  currency: string;
  bankAccounts: number;
  pricePerAccount: number;
  period: string;
  periodSavings: number;
  periodSavingsPercent: number;
  priceKey: string;
}
@Injectable({
  providedIn: 'root'
})
export class StripeService {

  stripe: Stripe;
  checkout: StripeEmbeddedCheckout;

  constructor(private http: HttpClient, private authService: AuthService) {

  }

  /*
    * Get the price tiers for the given number of bank accounts and period
  */
  public async getPriceTiers(numberOfBankAccounts: number, priceInterval: string) {
    let token = await this.authService.getAuthToken();
    let url = environment.systemAPI + '/public/pricing';
    let headers = {
      'Authorization': token
    };
    
    url += '?accounts=' + numberOfBankAccounts;
    url += '&period=' + priceInterval;

    return this.http.get(url, { headers: headers }).toPromise() as Promise<PriceTier>;
  }
  /*
    Init Stripe
  */
  public async getStripe() {
    if (this.stripe) return this.stripe;
    this.stripe = await loadStripe(environment.stripeKey);
    return this.stripe;
  }
  /*
    Checkout a Stripe Price as embedded checkout
  */
  public checkoutProduct(product: string, quantity: number = 1, projectId: string, element: HTMLElement) {
    return new Promise(async (resolve, reject) => {
      const stripe = await this.getStripe();
      try {
        const { clientSecret } = await this.createCheckoutSession(product, quantity, projectId);
        const checkout = await stripe.initEmbeddedCheckout({
          clientSecret,
        });
        checkout.mount(element);
        this.checkout = checkout;
        return resolve(checkout);
      } catch (e) {
        console.log(e);
        return reject(e);
      }
    });
  }

  /*
    Change Product via product portal
  */
  public async changeProduct(product: string, quantity: number = 1, projectId: string) {
    let token = await this.authService.getAuthToken();
    let url = environment.systemAPI + '/' + projectId + '/stripe/customer-portal/update-subscription';
    
    let body = {
      product: product,
      quantity: quantity
    }

    let headers = {
      'Authorization': token
    };
    return this.http.post(url, body, { headers: headers }).toPromise() as Promise<any>;
  }

  /*
    Create a Stripe Checkout Session
  */
  public async createCheckoutSession(product: string, quantity: number,projectId: string): Promise<StripeCheckoutSession> {
    let token = await this.authService.getAuthToken();
    let url = environment.systemAPI + '/' + projectId + '/upgrade-checkout';
    let body = {
      product: product,
      quantity: quantity
    }
    let headers = {
      'Authorization': token
    };
    return this.http.post(url, body, { headers: headers }).toPromise() as Promise<StripeCheckoutSession>;
  }

  /*
    Get the status of the checkout session
  */
  public async getCheckoutStatus(sessionId: string, projectId: string) {
    let token = await this.authService.getAuthToken();
    let url = environment.systemAPI + '/' + projectId + '/upgrade-checkout?sessionId=' + sessionId;
    let headers = {
      'Authorization': token
    };
    return this.http.get(url, { headers: headers }).toPromise() as Promise<any>;
  }
  /*
    Cancel the checkout session
  */
  public async cancelCheckoutSession() {
    if (!this.checkout) return;
    this.checkout.destroy();
  }

  /*
    Get a fresh Stripe Customer Portal link
  */
  public async getCustomerPortalLink(projectId: string, deeplink: string = "") {
    let token = await this.authService.getAuthToken();
    let url = environment.systemAPI + '/' + projectId + '/stripe/customer-portal';
    const deepLinks = ['portal', 'update-payment-method', 'cancel-subscription', 'update-subscription'];
    if(deeplink && deepLinks.indexOf(deeplink) != -1) url += '/' + deeplink;
    let headers = {
      'Authorization': token
    };
    return this.http.get(url, { headers: headers }).toPromise() as Promise<any>;
  }


}
